import React, { useEffect, useState } from 'react';
import Header from '../../components/ui/Header';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import KPICard from './components/KPICard';
import ChartSection from './components/ChartSection';
import QuickActions from './components/QuickActions';
import RecentActivity from './components/RecentActivity';
import AlertBanner from './components/AlertBanner';
import TopCustomersCard from './components/TopCustomersCard';
import InvoiceStatusCard from './components/InvoiceStatusCard';
import AccountSummaryCard from './components/AccountSummaryCard';
import { getDashboardData } from '../../services/dashboardService';
import { formatCurrency } from '../../utils/currency';
import { useCurrentCompany } from '../../hooks/useCompany';

const Dashboard = () => {
  const { data: currentCompany, isLoading: companyLoading } = useCurrentCompany();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentCompany && !companyLoading) {
      loadDashboardData();
    }
  }, [currentCompany, companyLoading]);

  const formatAmountWithCurrency = (amount) => {
    if (!currentCompany?.currency) {
      return formatCurrency(amount);
    }
    
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currentCompany.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      console.warn('Invalid currency code:', currentCompany.currency, 'falling back to formatCurrency');
      return formatCurrency(amount);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Get current year date range
      const now = new Date();
      const startDate = `${now.getFullYear()}-01-01`;
      const endDate = `${now.getFullYear()}-12-31`;
      
      const data = await getDashboardData(currentCompany?.id, startDate, endDate);
      setDashboardData(data);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getKPICards = () => {
    if (!dashboardData?.financialMetrics) {
      return [];
    }

    const metrics = dashboardData.financialMetrics;
    const cashFlowData = dashboardData.additionalKpis?.cash_flow_summary 
      ? JSON.parse(dashboardData.additionalKpis.cash_flow_summary) 
      : {};

    return [
      {
        title: 'Total Invoiced',
        value: formatAmountWithCurrency(parseFloat(metrics.total_invoiced || 0)),
        change: `${metrics.total_payment_count || 0} payments`,
        changeType: 'neutral',
        icon: 'FileText',
        iconColor: 'primary',
        trend: dashboardData.periodInfo?.period_description || 'this period'
      },
      {
        title: 'Total Collected',
        value: formatAmountWithCurrency(parseFloat(metrics.total_collected || 0)),
        change: `${metrics.active_customers || 0} customers`,
        changeType: 'positive',
        icon: 'DollarSign',
        iconColor: 'success',
        trend: dashboardData.periodInfo?.period_description || 'this period'
      },
      {
        title: 'Outstanding',
        value: formatAmountWithCurrency(parseFloat(metrics.outstanding_receivables || 0)),
        change: metrics.overdue_amount > 0 ? `${formatAmountWithCurrency(parseFloat(metrics.overdue_amount))} overdue` : 'No overdue',
        changeType: metrics.overdue_amount > 0 ? 'negative' : 'positive',
        icon: 'Clock',
        iconColor: metrics.overdue_amount > 0 ? 'error' : 'secondary',
        trend: 'receivables'
      },
      {
        title: 'Net Cash Flow',
        value: formatAmountWithCurrency(parseFloat(cashFlowData.net_cashflow || 0)),
        change: `${formatAmountWithCurrency(parseFloat(cashFlowData.cash_inflow || 0))} in`,
        changeType: parseFloat(cashFlowData.net_cashflow || 0) >= 0 ? 'positive' : 'negative',
        icon: 'TrendingUp',
        iconColor: parseFloat(cashFlowData.net_cashflow || 0) >= 0 ? 'success' : 'error',
        trend: 'cash flow'
      }
    ];
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-[1920px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-6 md:mb-8">
            <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-2">
              Dashboard
            </h1>
            <p className="font-body text-base md:text-lg text-muted-foreground">
              {dashboardData?.periodInfo?.period_description 
                ? `Financial overview for ${dashboardData.periodInfo.period_description}`
                : 'Welcome back! Here\'s your financial overview'}
              {currentCompany?.name && ` - ${currentCompany.name}`}
              {currentCompany?.currency && ` (${currentCompany.currency})`}
            </p>
          </div>

          <div className="mb-6 md:mb-8">
            <AlertBanner />
          </div>

          {loading || companyLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
                <p className="mt-4 text-muted-foreground">
                  {companyLoading ? 'Loading company data...' : 'Loading dashboard...'}
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
                {getKPICards().map((kpi, index) => (
                  <KPICard key={index} {...kpi} />
                ))}
              </div>

              {/* Charts Section */}
              <div className="mb-6 md:mb-8">
                <ChartSection />
              </div>

              {/* Invoice Status and Account Summary */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
                <InvoiceStatusCard 
                  invoiceBreakdown={dashboardData?.invoiceBreakdown || []}
                  currency={currentCompany?.currency || null}
                />
                <AccountSummaryCard 
                  accountSummary={dashboardData?.accountSummary || []}
                  currency={currentCompany?.currency || null}
                />
              </div>

              {/* Recent Activity and Top Customers */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
                <div className="lg:col-span-2">
                  <RecentActivity />
                </div>
                <div>
                  <TopCustomersCard 
                    customers={dashboardData?.topCustomers || []}
                    currency={currentCompany?.currency || null}
                  />
                </div>
              </div>

              {/* Quick Actions */}
              <div className="mb-6 md:mb-8">
                <QuickActions />
              </div>
            </>
          )}
        </div>
      </main>
      <QuickActionToolbar actions={[]} />
    </div>
  );
};

export default Dashboard;